import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { User, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(private _http: HttpClient, private _toastrService: ToastrService) {
    console.log('AuthenticationService: Initializing...');

    const currentUserItem = localStorage.getItem('currentUser');
    console.log('AuthenticationService: Current user item from localStorage:', currentUserItem);

    if (currentUserItem) {
      try {
        const decodedCurrentUser = JSON.parse(atob(currentUserItem));
        console.log('AuthenticationService: Decoded user:', decodedCurrentUser);
        this.currentUserSubject = new BehaviorSubject<User>(decodedCurrentUser);
      } catch (error) {
        console.error('AuthenticationService: Error decoding user:', error);
        // Clear corrupted data
        localStorage.removeItem('currentUser');
        this.currentUserSubject = new BehaviorSubject<User>(null);
      }
    } else {
      console.log('AuthenticationService: No user found in localStorage');
      this.currentUserSubject = new BehaviorSubject<User>(null);
    }

    this.currentUser = this.currentUserSubject.asObservable();
    console.log('AuthenticationService: Initialization complete');
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Admin;
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string) {
    return this._http
      .post<any>(`${environment.apiUrl}/users/authenticate`, { email, password })
      .pipe(
        map(user => {
          // login successful if there's a jwt token in the response
          if (user && user.token) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', btoa(JSON.stringify(user)));

            // Display welcome toast!
            setTimeout(() => {
              this._toastrService.success(
                'You have successfully logged in as an ' +
                user.role +
                ' user to Vuexy. Now you can start to explore. Enjoy! 🎉',
                '👋 Welcome, ' + user.firstName + '!',
                { toastClass: 'toast ngx-toastr', closeButton: true }
              );
            }, 2500);

            // notify
            this.currentUserSubject.next(user);
          }

          return user;
        })
      );
  }

  /**
   * User logout
   *
   */
  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    // notify
    this.currentUserSubject.next(null);
  }
}
